import { AlertCircle, AlertTriangle, Cake } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

const formatAlertDate = (value) => {
  if (!value) return '—';
  const [year, month, day] = String(value).slice(0, 10).split('-');
  if (year && month && day) return `${day}/${month}`;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return String(value);
  return `${String(parsed.getDate()).padStart(2, '0')}/${String(parsed.getMonth() + 1).padStart(2, '0')}`;
};

export const PayrollAlertsPanel = ({
  incompleteEmployees = 0,
  hasIrpsTableForSelectedYear,
  periodYear,
  onResolveIrpsGap,
  configSubmitting = false,
  attendanceFinalization = null,
  birthdayAlerts = [],
  showBirthdayAlerts = false,
  birthdayReferenceDate = '',
  birthdayTimezoneLabel = '',
  onDismissBirthdayAlerts,
}) => (
  <div className="space-y-2.5" data-testid="payroll-alerts-panel">
    {attendanceFinalization && !attendanceFinalization.is_finalized && (
      <div className="flex items-start gap-3 rounded-xl border border-orange-200 bg-orange-50 px-3 py-2.5" data-testid="payroll-attendance-finalization-alert">
        <AlertTriangle className="mt-0.5 h-5 w-5 text-orange-600" />
        <div className="flex-1">
          <p className="text-sm font-medium text-orange-800">Assiduidade por finalizar</p>
          <p className="text-xs text-orange-700" data-testid="payroll-attendance-finalization-message">{attendanceFinalization.message}</p>
          <div className="mt-2 flex flex-wrap gap-1.5 text-[11px]">
            <Badge variant="outline" className="border-orange-200 bg-white text-orange-700" data-testid="payroll-attendance-finalization-status">Estado: {attendanceFinalization.status_label}</Badge>
            <Badge variant="outline" className="border-orange-200 bg-white text-orange-700" data-testid="payroll-attendance-finalization-active-workers">Activos: {attendanceFinalization.active_employee_count || 0}</Badge>
            <Badge variant="outline" className="border-orange-200 bg-white text-orange-700" data-testid="payroll-attendance-finalization-workers-with-activity">Com actividade: {attendanceFinalization.employees_with_activity_count || 0}</Badge>
          </div>
        </div>
      </div>
    )}

    {showBirthdayAlerts && birthdayAlerts.length > 0 && (
      <div className="rounded-xl border border-slate-200 bg-white px-3 py-3 shadow-sm" data-testid="payroll-birthday-alerts-panel">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <div>
            <p className="text-sm font-semibold text-slate-900">Alertas de aniversário RH</p>
            <p className="text-xs text-slate-500">Avisos visíveis para Admin/RH conforme as janelas aprovadas e o fuso operacional activo.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {birthdayTimezoneLabel ? (
              <Badge variant="outline" className="bg-slate-50 text-slate-700" data-testid="payroll-birthday-timezone-badge">{birthdayTimezoneLabel}</Badge>
            ) : null}
            {birthdayReferenceDate ? (
              <Badge variant="outline" className="bg-slate-50 text-slate-700" data-testid="payroll-birthday-reference-date-badge">Data operacional: {formatAlertDate(birthdayReferenceDate)}</Badge>
            ) : null}
            <Badge variant="outline" className="bg-slate-50 text-slate-700" data-testid="payroll-birthday-count-badge">{birthdayAlerts.length} alerta(s)</Badge>
            <Button type="button" variant="outline" size="sm" onClick={onDismissBirthdayAlerts} data-testid="payroll-birthday-dismiss-button">
              Fechar mensagem
            </Button>
          </div>
        </div>

        <div className="mt-3 grid gap-2 lg:grid-cols-2 xl:grid-cols-3">
          {birthdayAlerts.map((alert) => (
            <div
              key={`${alert.employee_id || alert.employee_number}-${alert.birthday_date}`}
              className={cn(
                'rounded-xl border px-3 py-2.5',
                alert.variant === 'birthday'
                  ? 'border-emerald-200 bg-emerald-50'
                  : 'border-amber-200 bg-amber-50',
              )}
              data-testid={`payroll-birthday-alert-${alert.employee_id || alert.employee_number}`}
            >
              <div className="flex items-start gap-2">
                <Cake className={cn('mt-0.5 h-4 w-4', alert.variant === 'birthday' ? 'text-emerald-600' : 'text-amber-600')} />
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-1.5">
                    <Badge variant="outline" className={cn('bg-white', alert.variant === 'birthday' ? 'border-emerald-200 text-emerald-700' : 'border-amber-200 text-amber-700')} data-testid={`payroll-birthday-trigger-${alert.employee_id || alert.employee_number}`}>
                      {alert.trigger_label}
                    </Badge>
                    <span className="text-[11px] text-slate-500" data-testid={`payroll-birthday-date-${alert.employee_id || alert.employee_number}`}>{formatAlertDate(alert.birthday_date)}</span>
                  </div>
                  <p className="mt-1 text-xs leading-relaxed text-slate-700" data-testid={`payroll-birthday-message-${alert.employee_id || alert.employee_number}`}>{alert.message}</p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    )}

    {incompleteEmployees > 0 && (
      <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2.5" data-testid="payroll-incomplete-dossiers-alert">
        <AlertTriangle className="mt-0.5 h-5 w-5 text-amber-600" />
        <div>
          <p className="text-sm font-medium text-amber-800">{incompleteEmployees} funcionário(s) com processo incompleto</p>
          <p className="text-xs text-amber-700">Complete o dossier digital (BI, NUIT, INSS, Banco) antes de processar o salário.</p>
        </div>
      </div>
    )}

    {!hasIrpsTableForSelectedYear && (
      <div className="flex items-start gap-3 rounded-xl border border-red-200 bg-red-50 px-3 py-2.5" data-testid="payroll-irps-alert">
        <AlertCircle className="mt-0.5 h-5 w-5 text-red-600" />
        <div className="flex-1">
          <p className="text-sm font-medium text-red-800">Tabela IRPS não configurada para {periodYear}</p>
          <p className="text-xs text-red-700">A folha pode ser gerada, mas deve ficar em revisão até carregar/activar a tabela IRPS oficial do ano seleccionado.</p>
        </div>
        <Button variant="outline" size="sm" onClick={onResolveIrpsGap} disabled={configSubmitting} data-testid="payroll-seed-irps-button">
          {configSubmitting ? 'A activar...' : `Activar oficial ${periodYear}`}
        </Button>
      </div>
    )}
  </div>
);